import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Eye,
  Plus,
  Search,
  Filter,
  Camera,
  Video,
  Mic,
  MicOff,
  FileText,
  AlertTriangle,
  CheckCircle2,
  Clock,
  MapPin,
  Building,
  User,
  Calendar,
  Download,
  Trash2,
  Edit,
  Sparkles,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Play,
  Pause,
  Upload,
  X,
  Loader2,
  Shield,
  Target,
  TrendingUp,
  FileSpreadsheet,
  FileType,
  Printer,
  ExternalLink,
  Image as ImageIcon,
  Film,
  MessageSquare,
  Wand2
} from 'lucide-react';
import PageHeader from './ui/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Professional header image
const HEADER_IMAGE_URL = "https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=1600&q=80";

// Feature highlights
const OBSERVATION_FEATURES = [
  { icon: Camera, label: "Photo Capture", description: "Upload images", color: "text-blue-500", bg: "bg-blue-50" },
  { icon: Video, label: "Video Upload", description: "Record evidence", color: "text-purple-500", bg: "bg-purple-50" },
  { icon: Mic, label: "Voice Notes", description: "AI transcription", color: "text-green-500", bg: "bg-green-50" },
  { icon: Sparkles, label: "AI Analysis", description: "Smart evaluation", color: "text-amber-500", bg: "bg-amber-50" }
];

// Categories
const CATEGORIES = [
  { value: 'fall_hazard', label: 'Fall Hazard' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'chemical', label: 'Chemical' },
  { value: 'fire', label: 'Fire' },
  { value: 'machinery', label: 'Machinery' },
  { value: 'ppe', label: 'PPE' },
  { value: 'housekeeping', label: 'Housekeeping' },
  { value: 'ergonomic', label: 'Ergonomic' },
  { value: 'confined_space', label: 'Confined Space' },
  { value: 'excavation', label: 'Excavation' },
  { value: 'scaffolding', label: 'Scaffolding' },
  { value: 'ladder', label: 'Ladder' },
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'struck_by', label: 'Struck By' },
  { value: 'caught_in', label: 'Caught In' },
  { value: 'other', label: 'Other' }
];

const SEVERITIES = [
  { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-700 border-red-200' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  { value: 'low', label: 'Low', color: 'bg-green-100 text-green-700 border-green-200' }
];

const STATUSES = [
  { value: 'open', label: 'Open', color: 'bg-blue-100 text-blue-700' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-amber-100 text-amber-700' },
  { value: 'resolved', label: 'Resolved', color: 'bg-green-100 text-green-700' },
  { value: 'closed', label: 'Closed', color: 'bg-gray-100 text-gray-700' }
];

const ObservationsPage = () => {
  const [observations, setObservations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedObservation, setSelectedObservation] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    company_id: '',
    category: 'other',
    severity: 'medium',
    text_notes: '',
    assigned_to: '',
    due_date: '',
    tags: ''
  });
  
  const [uploadedMedia, setUploadedMedia] = useState([]);
  const [voiceNotes, setVoiceNotes] = useState([]);

  useEffect(() => {
    fetchObservations();
    fetchCompanies();
    fetchUsers();
    fetchStats();
  }, [searchQuery, filterCategory, filterSeverity, filterStatus, sortBy, sortOrder]);

  const fetchObservations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (filterCategory !== 'all') params.append('category', filterCategory);
      if (filterSeverity !== 'all') params.append('severity', filterSeverity);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
      
      const response = await axios.get(`${API_URL}/api/observations?${params.toString()}`);
      setObservations(response.data.observations || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load observations",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/companies`);
      setCompanies(response.data || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/users`);
      // Handle both array and object with users property
      const userData = response.data;
      if (Array.isArray(userData)) {
        setUsers(userData);
      } else if (userData && Array.isArray(userData.users)) {
        setUsers(userData.users);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/observations/stats/summary`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleCreateObservation = async () => {
    if (!formData.title || !formData.location || !formData.company_id) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    try {
      setLoading(true);
      
      // Create observation
      const response = await axios.post(`${API_URL}/api/observations`, {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : []
      });
      
      const observationId = response.data.observation.id;
      
      // Upload media files
      for (const media of uploadedMedia) {
        const mediaFormData = new FormData();
        mediaFormData.append('file', media.file);
        
        await axios.post(
          `${API_URL}/api/observations/${observationId}/media`,
          mediaFormData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      }
      
      // Upload voice notes
      for (const voice of voiceNotes) {
        const voiceFormData = new FormData();
        voiceFormData.append('audio_file', voice.blob, 'voice_note.webm');
        
        await axios.post(
          `${API_URL}/api/observations/${observationId}/voice-note`,
          voiceFormData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      }
      
      toast({
        title: "Success",
        description: "Observation created successfully"
      });
      
      setShowCreateDialog(false);
      resetForm();
      fetchObservations();
      fetchStats();
      
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create observation",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRunAnalysis = async (observationId) => {
    try {
      setAnalyzing(true);
      
      const response = await axios.post(`${API_URL}/api/observations/${observationId}/analyze`);
      
      toast({
        title: "Analysis Complete",
        description: "AI analysis has been generated"
      });
      
      // Refresh observation details
      if (selectedObservation?.id === observationId) {
        const updated = await axios.get(`${API_URL}/api/observations/${observationId}`);
        setSelectedObservation(updated.data.observation);
      }
      
      fetchObservations();
      
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to run analysis",
        variant: "destructive"
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const handleExport = async (observationId, format) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/observations/${observationId}/export/${format}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `observation_${observationId}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({
        title: "Success",
        description: `Exported as ${format.toUpperCase()}`
      });
      
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to export observation",
        variant: "destructive"
      });
    }
  };

  const handleDeleteObservation = async (observationId) => {
    if (!window.confirm('Are you sure you want to delete this observation?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/observations/${observationId}`);
      
      toast({
        title: "Success",
        description: "Observation deleted"
      });
      
      setShowDetailDialog(false);
      fetchObservations();
      fetchStats();
      
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete observation",
        variant: "destructive"
      });
    }
  };

  const handleUpdateStatus = async (observationId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/observations/${observationId}`, {
        status: newStatus
      });
      
      toast({
        title: "Success",
        description: "Status updated"
      });
      
      fetchObservations();
      fetchStats();
      
      if (selectedObservation?.id === observationId) {
        const updated = await axios.get(`${API_URL}/api/observations/${observationId}`);
        setSelectedObservation(updated.data.observation);
      }
      
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive"
      });
    }
  };

  // Media handling
  const handleFileUpload = (e, type) => {
    const files = Array.from(e.target.files);
    const newMedia = files.map(file => ({
      id: Date.now() + Math.random(),
      file,
      type,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    setUploadedMedia([...uploadedMedia, ...newMedia]);
  };

  const removeMedia = (id) => {
    setUploadedMedia(uploadedMedia.filter(m => m.id !== id));
  };

  // Voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];
      
      recorder.ondataavailable = (e) => {
        chunks.push(e.data);
      };
      
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        setVoiceNotes([...voiceNotes, {
          id: Date.now(),
          blob,
          preview: URL.createObjectURL(blob)
        }]);
        stream.getTracks().forEach(track => track.stop());
      };
      
      recorder.start();
      setMediaRecorder(recorder);
      setAudioChunks(chunks);
      setIsRecording(true);
      
    } catch (error) {
      toast({
        title: "Error",
        description: "Could not access microphone",
        variant: "destructive"
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorder) {
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };

  const removeVoiceNote = (id) => {
    setVoiceNotes(voiceNotes.filter(v => v.id !== id));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      location: '',
      company_id: '',
      category: 'other',
      severity: 'medium',
      text_notes: '',
      assigned_to: '',
      due_date: '',
      tags: ''
    });
    setUploadedMedia([]);
    setVoiceNotes([]);
  };

  const getSeverityBadge = (severity) => {
    const sev = SEVERITIES.find(s => s.value === severity);
    return sev ? sev.color : 'bg-gray-100 text-gray-700';
  };

  const getStatusBadge = (status) => {
    const stat = STATUSES.find(s => s.value === status);
    return stat ? stat.color : 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-4 md:space-y-6">
        {/* Unified Page Header */}
        <PageHeader
          title="Safety Observations"
          subtitle="Capture, analyze, and track safety observations with AI-powered evaluation"
          icon={Eye}
          variant="purple"
          onRefresh={() => { fetchObservations(); fetchStats(); }}
          loading={loading}
          actions={
            <Button 
              onClick={() => setShowCreateDialog(true)}
              size="sm"
              className="bg-white/20 hover:bg-white/30 text-white border-0"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Observation
            </Button>
          }
          stats={[
            { icon: Eye, label: 'Total', value: stats?.total || 0, color: 'purple' },
            { icon: AlertTriangle, label: 'Open', value: stats?.by_status?.open || 0, color: 'red' },
            { icon: Clock, label: 'In Progress', value: stats?.by_status?.in_progress || 0, color: 'yellow' },
            { icon: CheckCircle2, label: 'Resolved', value: stats?.by_status?.resolved || 0, color: 'green' }
          ]}
        />

        {/* Feature Cards Row - Horizontal scroll on mobile */}
        <div className="flex gap-2 md:gap-4 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap md:justify-between bg-white rounded-xl md:rounded-2xl p-3 md:p-4 shadow-sm">
          {OBSERVATION_FEATURES.map((feature, idx) => (
            <div 
              key={idx}
              className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-xl ${feature.bg} cursor-pointer hover:shadow-md transition-all flex-shrink-0`}
            >
              <feature.icon className={`w-4 md:w-5 h-4 md:h-5 ${feature.color}`} />
              <div>
                <p className="text-xs md:text-sm font-medium text-gray-900">{feature.label}</p>
                <p className="text-xs text-gray-500 hidden md:block">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Statistics Cards - 2 columns on mobile, 4 on desktop */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <Card className="rounded-xl md:rounded-2xl shadow-sm border-t-4 border-t-indigo-500 hover:shadow-lg transition-all">
            <CardContent className="pt-4 md:pt-6 p-3 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm text-gray-500 font-medium">Total</p>
                  <p className="text-xl md:text-3xl font-bold text-gray-900">{stats?.total || 0}</p>
                  <p className="text-xs text-gray-400 mt-1 hidden md:block">all records</p>
                </div>
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
                  <Eye className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-xl md:rounded-2xl shadow-sm border-t-4 border-t-red-500 hover:shadow-lg transition-all">
            <CardContent className="pt-4 md:pt-6 p-3 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm text-gray-500 font-medium">Critical</p>
                  <p className="text-xl md:text-3xl font-bold text-gray-900">{stats?.by_severity?.critical || 0}</p>
                  <p className="text-xs text-gray-400 mt-1 hidden md:block">high priority</p>
                </div>
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center shadow-lg">
                  <AlertTriangle className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-xl md:rounded-2xl shadow-sm border-t-4 border-t-amber-500 hover:shadow-lg transition-all">
            <CardContent className="pt-4 md:pt-6 p-3 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm text-gray-500 font-medium">In Progress</p>
                  <p className="text-xl md:text-3xl font-bold text-gray-900">{stats?.by_status?.in_progress || 0}</p>
                  <p className="text-xs text-gray-400 mt-1 hidden md:block">being addressed</p>
                </div>
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg">
                  <Clock className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-xl md:rounded-2xl shadow-sm border-t-4 border-t-green-500 hover:shadow-lg transition-all">
            <CardContent className="pt-4 md:pt-6 p-3 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm text-gray-500 font-medium">Resolved</p>
                  <p className="text-xl md:text-3xl font-bold text-gray-900">{stats?.by_status?.resolved || 0}</p>
                  <p className="text-xs text-gray-400 mt-1 hidden md:block">completed</p>
                </div>
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg">
                  <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters and Search - Stack on mobile */}
        <Card className="rounded-xl md:rounded-2xl shadow-sm">
          <CardContent className="p-3 md:p-4">
            <div className="flex flex-col md:flex-row gap-3 md:gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search observations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:flex gap-2 md:gap-3">
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="w-full md:w-[130px]">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={filterSeverity} onValueChange={setFilterSeverity}>
                  <SelectTrigger className="w-full md:w-[120px]">
                    <SelectValue placeholder="Severity" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Severities</SelectItem>
                    {SEVERITIES.map(sev => (
                      <SelectItem key={sev.value} value={sev.value}>{sev.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-full md:w-[120px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {STATUSES.map(stat => (
                      <SelectItem key={stat.value} value={stat.value}>{stat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={`${sortBy}-${sortOrder}`} onValueChange={(val) => {
                  const [by, order] = val.split('-');
                  setSortBy(by);
                  setSortOrder(order);
                }}>
                  <SelectTrigger className="w-full md:w-[140px]">
                    <SelectValue placeholder="Sort" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="created_at-desc">Newest First</SelectItem>
                    <SelectItem value="created_at-asc">Oldest First</SelectItem>
                    <SelectItem value="severity-desc">Severity (High-Low)</SelectItem>
                    <SelectItem value="severity-asc">Severity (Low-High)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Observations List */}
        <div className="grid gap-4">
          {loading ? (
            <Card className="rounded-2xl">
              <CardContent className="p-8 text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-indigo-500" />
                <p className="mt-2 text-gray-500">Loading observations...</p>
              </CardContent>
            </Card>
          ) : observations.length === 0 ? (
            <Card className="rounded-2xl">
              <CardContent className="p-12 text-center">
                <Eye className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Observations Found</h3>
                <p className="text-gray-500 mb-4">Start capturing safety observations to build your records</p>
                <Button onClick={() => setShowCreateDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Observation
                </Button>
              </CardContent>
            </Card>
          ) : (
            observations.map(obs => (
              <Card 
                key={obs.id} 
                className="rounded-2xl hover:shadow-lg transition-all cursor-pointer"
                onClick={() => {
                  setSelectedObservation(obs);
                  setShowDetailDialog(true);
                }}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={getSeverityBadge(obs.severity)}>
                          {obs.severity?.toUpperCase()}
                        </Badge>
                        <Badge className={getStatusBadge(obs.status)}>
                          {obs.status?.replace('_', ' ').toUpperCase()}
                        </Badge>
                        <Badge variant="outline">
                          {obs.category?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </Badge>
                        {obs.ai_analysis && (
                          <Badge className="bg-purple-100 text-purple-700">
                            <Sparkles className="w-3 h-3 mr-1" />
                            AI Analyzed
                          </Badge>
                        )}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{obs.title}</h3>
                      <p className="text-gray-600 text-sm line-clamp-2">{obs.description || 'No description'}</p>
                      
                      <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {obs.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Building className="w-4 h-4" />
                          {obs.company_name || 'N/A'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(obs.created_at).toLocaleDateString()}
                        </span>
                        {obs.assigned_to_name && (
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {obs.assigned_to_name}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 ml-4">
                      {obs.media?.length > 0 && (
                        <div className="flex items-center gap-1 text-gray-400">
                          <ImageIcon className="w-4 h-4" />
                          <span className="text-xs">{obs.media.length}</span>
                        </div>
                      )}
                      {obs.voice_notes?.length > 0 && (
                        <div className="flex items-center gap-1 text-gray-400">
                          <Mic className="w-4 h-4" />
                          <span className="text-xs">{obs.voice_notes.length}</span>
                        </div>
                      )}
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Create Observation Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-indigo-600" />
                New Safety Observation
              </DialogTitle>
              <DialogDescription>
                Capture a safety observation with photos, videos, and voice notes
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Title *</Label>
                  <Input
                    placeholder="Brief title of the observation"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                  />
                </div>
                
                <div>
                  <Label>Company/Jobsite *</Label>
                  <Select 
                    value={formData.company_id} 
                    onValueChange={(val) => setFormData({...formData, company_id: val})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select company" />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Location *</Label>
                  <Input
                    placeholder="Specific location (e.g., Building A, Floor 2)"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                  />
                </div>
                
                <div>
                  <Label>Category</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(val) => setFormData({...formData, category: val})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Severity</Label>
                  <Select 
                    value={formData.severity} 
                    onValueChange={(val) => setFormData({...formData, severity: val})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SEVERITIES.map(sev => (
                        <SelectItem key={sev.value} value={sev.value}>{sev.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Assign To</Label>
                  <Select 
                    value={formData.assigned_to || "unassigned"} 
                    onValueChange={(val) => setFormData({...formData, assigned_to: val === "unassigned" ? "" : val})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select team member" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.username}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                  />
                </div>
              </div>
              
              {/* Description */}
              <div>
                <Label>Description</Label>
                <Textarea
                  placeholder="Detailed description of the safety observation..."
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                />
              </div>
              
              {/* Media Upload */}
              <div className="space-y-3">
                <Label>Photos & Videos</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Add Photos
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => videoInputRef.current?.click()}
                  >
                    <Video className="w-4 h-4 mr-2" />
                    Add Video
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => handleFileUpload(e, 'image')}
                  />
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/*"
                    className="hidden"
                    onChange={(e) => handleFileUpload(e, 'video')}
                  />
                </div>
                
                {uploadedMedia.length > 0 && (
                  <div className="grid grid-cols-4 gap-2">
                    {uploadedMedia.map(media => (
                      <div key={media.id} className="relative group">
                        {media.type === 'image' ? (
                          <img 
                            src={media.preview} 
                            alt={media.name}
                            className="w-full h-24 object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-full h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                            <Film className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                        <button
                          onClick={() => removeMedia(media.id)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Voice Notes */}
              <div className="space-y-3">
                <Label>Voice Notes</Label>
                <div className="flex gap-2">
                  {!isRecording ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={startRecording}
                    >
                      <Mic className="w-4 h-4 mr-2" />
                      Record Voice Note
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={stopRecording}
                    >
                      <MicOff className="w-4 h-4 mr-2" />
                      Stop Recording
                    </Button>
                  )}
                </div>
                
                {voiceNotes.length > 0 && (
                  <div className="space-y-2">
                    {voiceNotes.map((voice, idx) => (
                      <div key={voice.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                        <Mic className="w-4 h-4 text-indigo-500" />
                        <span className="text-sm">Voice Note {idx + 1}</span>
                        <audio src={voice.preview} controls className="h-8 flex-1" />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeVoiceNote(voice.id)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Text Notes */}
              <div>
                <Label>Additional Notes</Label>
                <Textarea
                  placeholder="Any additional notes or context..."
                  value={formData.text_notes}
                  onChange={(e) => setFormData({...formData, text_notes: e.target.value})}
                  rows={3}
                />
              </div>
              
              {/* Tags */}
              <div>
                <Label>Tags (comma separated)</Label>
                <Input
                  placeholder="e.g., urgent, follow-up, training-needed"
                  value={formData.tags}
                  onChange={(e) => setFormData({...formData, tags: e.target.value})}
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
                Cancel
              </Button>
              <Button onClick={handleCreateObservation} disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create Observation
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            {selectedObservation && (
              <>
                <DialogHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <DialogTitle className="text-xl">{selectedObservation.title}</DialogTitle>
                      <DialogDescription className="mt-1">
                        Created on {new Date(selectedObservation.created_at).toLocaleString()} by {selectedObservation.created_by_name}
                      </DialogDescription>
                    </div>
                    <div className="flex gap-2">
                      <Badge className={getSeverityBadge(selectedObservation.severity)}>
                        {selectedObservation.severity?.toUpperCase()}
                      </Badge>
                      <Badge className={getStatusBadge(selectedObservation.status)}>
                        {selectedObservation.status?.replace('_', ' ').toUpperCase()}
                      </Badge>
                    </div>
                  </div>
                </DialogHeader>
                
                <Tabs defaultValue="details" className="mt-4">
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="details">Details</TabsTrigger>
                    <TabsTrigger value="media">Media ({selectedObservation.media?.length || 0})</TabsTrigger>
                    <TabsTrigger value="analysis">AI Analysis</TabsTrigger>
                    <TabsTrigger value="actions">Actions</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="details" className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Location</p>
                        <p className="font-medium">{selectedObservation.location}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Company</p>
                        <p className="font-medium">{selectedObservation.company_name}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Category</p>
                        <p className="font-medium">{selectedObservation.category?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Assigned To</p>
                        <p className="font-medium">{selectedObservation.assigned_to_name || 'Unassigned'}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-1">
                      <p className="text-sm text-gray-500">Description</p>
                      <p className="text-gray-700">{selectedObservation.description || 'No description provided'}</p>
                    </div>
                    
                    {selectedObservation.text_notes && (
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Notes</p>
                        <p className="text-gray-700">{selectedObservation.text_notes}</p>
                      </div>
                    )}
                    
                    {selectedObservation.voice_notes?.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm text-gray-500">Voice Transcriptions</p>
                        {selectedObservation.voice_notes.map((vn, idx) => (
                          <div key={vn.id} className="p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm font-medium text-gray-700">Voice Note {idx + 1}</p>
                            <p className="text-gray-600 mt-1">{vn.transcription}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="media" className="mt-4">
                    {selectedObservation.media?.length > 0 ? (
                      <div className="grid grid-cols-2 gap-4">
                        {selectedObservation.media.map((m, idx) => (
                          <div key={m.id} className="space-y-2">
                            {m.type === 'image' ? (
                              <img 
                                src={m.url} 
                                alt={m.filename}
                                className="w-full h-48 object-cover rounded-lg"
                              />
                            ) : (
                              <video 
                                src={m.url} 
                                controls
                                className="w-full h-48 rounded-lg bg-slate-200"
                              />
                            )}
                            {m.ai_analysis && (
                              <div className="p-2 bg-purple-50 rounded-lg">
                                <p className="text-xs font-medium text-purple-700">AI Analysis</p>
                                <p className="text-xs text-purple-600 mt-1 line-clamp-3">{m.ai_analysis.analysis}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <ImageIcon className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                        <p>No media attached</p>
                      </div>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="analysis" className="mt-4">
                    {selectedObservation.ai_analysis ? (
                      <div className="space-y-4">
                        <div className="flex items-center gap-4">
                          <Badge className="text-lg py-1 px-3" variant={
                            selectedObservation.ai_analysis.risk_level === 'Critical' ? 'destructive' :
                            selectedObservation.ai_analysis.risk_level === 'High' ? 'warning' : 'secondary'
                          }>
                            Risk Level: {selectedObservation.ai_analysis.risk_level}
                          </Badge>
                          <Badge variant="outline">
                            {selectedObservation.ai_analysis.compliance_status}
                          </Badge>
                        </div>
                        
                        {selectedObservation.ai_analysis.hazards_identified?.length > 0 && (
                          <div>
                            <h4 className="font-medium text-gray-900 mb-2">Hazards Identified</h4>
                            <ul className="space-y-1">
                              {selectedObservation.ai_analysis.hazards_identified.map((h, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                                  <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                                  {h}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {selectedObservation.ai_analysis.osha_violations?.length > 0 && (
                          <div>
                            <h4 className="font-medium text-gray-900 mb-2">OSHA Violations</h4>
                            <ul className="space-y-1">
                              {selectedObservation.ai_analysis.osha_violations.map((v, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-red-700">
                                  <Shield className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                                  {v}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {selectedObservation.ai_analysis.immediate_actions?.length > 0 && (
                          <div>
                            <h4 className="font-medium text-gray-900 mb-2">Immediate Actions</h4>
                            <ul className="space-y-1">
                              {selectedObservation.ai_analysis.immediate_actions.map((a, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                                  <Target className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                                  {a}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {selectedObservation.ai_analysis.recommendations?.length > 0 && (
                          <div>
                            <h4 className="font-medium text-gray-900 mb-2">Recommendations</h4>
                            <ul className="space-y-1">
                              {selectedObservation.ai_analysis.recommendations.map((r, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                  {r}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {selectedObservation.ai_analysis.root_cause_analysis && (
                          <div>
                            <h4 className="font-medium text-gray-900 mb-2">Root Cause Analysis</h4>
                            <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                              {selectedObservation.ai_analysis.root_cause_analysis}
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Sparkles className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                        <p className="text-gray-500 mb-4">AI analysis not yet performed</p>
                        <Button 
                          onClick={() => handleRunAnalysis(selectedObservation.id)}
                          disabled={analyzing}
                        >
                          {analyzing ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Wand2 className="w-4 h-4 mr-2" />
                          )}
                          Run AI Analysis
                        </Button>
                      </div>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="actions" className="mt-4">
                    <div className="space-y-4">
                      {/* Status Update */}
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Update Status</h4>
                        <div className="flex gap-2">
                          {STATUSES.map(status => (
                            <Button
                              key={status.value}
                              variant={selectedObservation.status === status.value ? "default" : "outline"}
                              size="sm"
                              onClick={() => handleUpdateStatus(selectedObservation.id, status.value)}
                            >
                              {status.label}
                            </Button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Run Analysis */}
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">AI Analysis</h4>
                        <Button 
                          onClick={() => handleRunAnalysis(selectedObservation.id)}
                          disabled={analyzing}
                        >
                          {analyzing ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Sparkles className="w-4 h-4 mr-2" />
                          )}
                          {selectedObservation.ai_analysis ? 'Re-run Analysis' : 'Run AI Analysis'}
                        </Button>
                      </div>
                      
                      {/* Export */}
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Export Report</h4>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            onClick={() => handleExport(selectedObservation.id, 'pdf')}
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            PDF
                          </Button>
                          <Button 
                            variant="outline"
                            onClick={() => handleExport(selectedObservation.id, 'csv')}
                          >
                            <FileSpreadsheet className="w-4 h-4 mr-2" />
                            CSV
                          </Button>
                          <Button 
                            variant="outline"
                            onClick={() => handleExport(selectedObservation.id, 'docx')}
                          >
                            <FileType className="w-4 h-4 mr-2" />
                            DOCX
                          </Button>
                        </div>
                      </div>
                      
                      {/* Delete */}
                      <div className="pt-4 border-t">
                        <Button 
                          variant="destructive"
                          onClick={() => handleDeleteObservation(selectedObservation.id)}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete Observation
                        </Button>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ObservationsPage;
